<template>
  <v-dialog v-model="showDialog" max-width="1200px" scrollable persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon icon="mdi-link-variant" class="mr-2"></v-icon>
        Link Subtask
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" @click="closeDialog" :disabled="isLinking"></v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text>
        <v-stepper v-model="currentStep" :items="stepperItems" alt-labels hide-actions>
          <template v-slot:item.1>
            <v-card flat>
              <v-card-title>Select Location</v-card-title>
              <v-card-text>
                <MiniMap
                  :drawing-id="selectedDrawingId"
                  :selected-subtask="selectedSubtask"
                  @location-selected="onLocationSelected"
                  @drawing-changed="onDrawingChanged"
                />
              </v-card-text>
            </v-card>
          </template>

          <template v-slot:item.2>
            <v-card flat>
              <v-card-title>Select Profile</v-card-title>
              <v-card-text>
                <v-select
                  v-model="selectedProfile"
                  :items="profileOptions"
                  label="Select Profile"
                  item-title="Title"
                  item-value="Id"
                  return-object
                  variant="outlined"
                  class="mb-4"
                  :disabled="profileOptions.length === 1"
                />
                <v-text-field
                  v-model="externalReference"
                  label="External Reference"
                  variant="outlined"
                />
              </v-card-text>
            </v-card>
          </template>
        </v-stepper>
      </v-card-text>

      <v-card-actions>
        <v-btn 
          v-if="currentStep > 1"
          color="primary" 
          variant="text" 
          @click="previousStep"
          :disabled="isLinking"
        >
          Previous
        </v-btn>
        <v-spacer />
        <v-btn 
          v-if="currentStep === stepperItems.length"
          color="primary" 
          variant="elevated" 
          @click="linkSubtask"
          :disabled="!canComplete || isLinking"
          :loading="isLinking"
        >
          Link Subtask
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import MiniMap from './MiniMap.vue'
import RandomIdGenerator from '@/utils/RandomIdGenerator'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  selectedSubtask: {
    type: Object,
    default: null
  },
  drawingId: {
    type: [String, Number],
    default: null
  }
})

// Emits
const emit = defineEmits(['update:show', 'subtask-linked'])

// Store
const store = useStore()
const conn = store.getters.getDBConnection
const apiClient = store.getters.getApiClient
const projectCode = computed(() => store.getters.getProjectCode)
const apiUrl = computed(() => store.getters.getApiUrl)
const idGenerator = new RandomIdGenerator()

// Data
const currentStep = ref(1)
const selectedLocation = ref(null)
const selectedProfile = ref(null)
const externalReference = ref('')
const profileOptions = ref([])
const currentDrawingId = ref(null)
const isLinking = ref(false)

// Computed
const showDialog = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value)
})

const selectedDrawingId = computed(() => props.drawingId)

const stepperItems = ref([
  {
    title: 'Select Location',
    value: 1
  },
  {
    title: 'Select Profile',
    value: 2
  }
])

const canProceed = computed(() => {
  if (currentStep.value === 1) {
    return selectedLocation.value !== null
  }
  return true
})

const canComplete = computed(() => {
  return selectedLocation.value !== null && selectedProfile.value !== null && currentStep.value === stepperItems.value.length
})

// Methods
const fetchProfileOptions = async () => {
  if (!props.selectedSubtask?.itemTemplateCode) return
  
  try {
    const templateCode = props.selectedSubtask.itemTemplateCode
    const isProcess = props.selectedSubtask.itemType === 'process'
    
    let query
    if (isProcess) {
      query = `
        SELECT pp.Id, pp.Title, pp.Code
        FROM process_profiles pp
        JOIN item_templates it ON pp.ProcessTemplateCode = it.Code
        WHERE it.Code = '${templateCode}'
        ORDER BY pp.Title
      `
    } else {
      query = `
        SELECT ip.Id, ip.Title, ip.Code
        FROM issue_profiles ip
        JOIN item_templates it ON ip.IssueTemplateCode = it.Code
        WHERE it.Code = '${templateCode}'
        ORDER BY ip.Title
      `
    }
    
    const result = await conn.query(query)
    const rows = result.toArray()
    profileOptions.value = rows.map(row => row.toJSON())
    
    // Auto-select if only one option
    if (profileOptions.value.length === 1) {
      selectedProfile.value = profileOptions.value[0]
    }
  } catch (error) {
    console.error('Error fetching profile options:', error)
    profileOptions.value = []
  }
}

const onLocationSelected = (location) => {
  selectedLocation.value = location
  // Fetch profile options when location is selected
  fetchProfileOptions()
  // Automatically advance to next step when location is selected
  if (currentStep.value === 1) {
    nextStep()
  }
}

const onDrawingChanged = (drawingId) => {
  currentDrawingId.value = drawingId
}

const nextStep = () => {
  if (currentStep.value < stepperItems.value.length) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

// Helper functions for item creation
const getItemTemplate = async (itemTemplateCode, type) => {
  if (!itemTemplateCode || !type) return null
  
  const templateType = type === 'process' ? 'ProcessTemplate' : 'IssueTemplate';

  try {
    const templateQuery = `
      SELECT Id, Code, Title, Type
      FROM item_templates
      WHERE Code = '${itemTemplateCode}' And Type = '${templateType}'
      LIMIT 1
    `
    const templateResult = await conn.query(templateQuery)
    const templateRows = templateResult.toArray()
    return templateRows.length > 0 ? templateRows[0].toJSON() : null
  } catch (error) {
    console.error('Error fetching item template:', error)
    return null
  }
}

const buildItemPayload = (itemId, location, drawing, itemTemplate, subtask) => {
  return {
    createdDate: new Date().toISOString(),
    receivedDate: '',
    syncedDate: '',
    createdByTeamId: '',
    createdByTeam: '',
    createdByUserId: '',
    createdByUser: '',
    progress: '',
    id: itemId,
    clientId: '',
    x: location.X + 75,
    y: location.Y + 75,
    categoryId: '',
    categoryTitle: '',
    subcategoryId: '',
    subcategoryTitle: '',
    processTemplateId: itemTemplate?.Id || '',
    processTemplateCode: itemTemplate?.Code || '',
    processTemplateTitle: itemTemplate?.Title || '',
    processProfileCode: selectedProfile.value?.Code || '',
    processProfileId: selectedProfile.value?.Id || '',
    processProfileTitle: '',
    tags: [],
    number: 0,
    customPositioning: false,
    icon: '',
    title: subtask.title || '',
    subtitle: '',
    thumbnail: '',
    label: '',
    drawingId: drawing.Id,
    drawingTitle: drawing.Title,
    drawingTags: [],
    locationId: location.Id,
    locationTitle: location.Title,
    locationExternalReference: location.ExternalReference || '',
    locationTags: [],
    externalReference: '',
    controlledByTeamId: '1',
    controlledByTeamTags: [],
    forms: [],
    formProgressItems: []
  }
}

const getApiEndpoint = (isProcess) => {
  return isProcess 
    ? `/api/${projectCode.value}/processes` 
    : `/api/${projectCode.value}/issues`
}

const buildSubtaskPatchPayload = (itemId, subtask) => {
  return [{
    taskId: subtask.taskId,
    subtaskId: subtask.id,
    itemId: itemId,
    leadByTeamId: ""
  }]
}

const buildViewerUrl = (isProcess, itemId, createdItem) => {
  // Remove 'api-' prefix from apiUrl if it exists
  const cleanApiUrl = apiUrl.value.replace(/^api-/, '')
  
  if (isProcess) {
    if (createdItem.forms && createdItem.forms.length > 0) {
      const firstFormId = createdItem.forms[0].id
      return `https://${cleanApiUrl}/${projectCode.value}/form-viewer/${firstFormId}`
    } else {
      console.warn('Process created but no forms found')
      return null
    }
  } else {
    return `https://${cleanApiUrl}/${projectCode.value}/issue-viewer/${itemId}`
  }
}

// Helper functions for item creation process
const getCurrentDrawing = async () => {
  const drawingId = currentDrawingId.value || props.drawingId
  const drawingQuery = `
    SELECT Id, Title, Url
    FROM drawings
    WHERE Id = '${drawingId}'
    LIMIT 1
  `
  const drawingResult = await conn.query(drawingQuery)
  const drawingRows = drawingResult.toArray()
  
  if (drawingRows.length === 0) {
    throw new Error('No current drawing selected')
  }
  
  return drawingRows[0].toJSON()
}

const createItemViaAPI = async (isProcess, endpoint, payload) => {
  const response = await apiClient.post(endpoint, payload)
  console.log('Item created successfully:', response)

  // Extract itemId based on response type
  const itemId = isProcess ? response.id : (Array.isArray(response) && response.length > 0 ? response[0].id : null)
  
  if (!itemId) {
    throw new Error('Failed to get item ID from response')
  }
  
  return itemId
}

const updateSubtaskWithItem = async (itemId) => {
  const subtaskPatchEndpoint = `/api/${projectCode.value}/tasks/subtasks`
  const subtaskPatchPayload = buildSubtaskPatchPayload(itemId, props.selectedSubtask)
  
  const subtaskResponse = await apiClient.patch(subtaskPatchEndpoint, subtaskPatchPayload)
  console.log('Subtask updated successfully:', subtaskResponse)
}

const retrieveCreatedItem = async (endpoint, itemId, isProcess) => {
  const getEndpoint = `${endpoint}/${itemId}`
  const getResponse = await apiClient.get(getEndpoint)
  console.log('Retrieved created item:', getResponse)
  
  // Handle different response formats
  return isProcess ? getResponse : (Array.isArray(getResponse) ? getResponse[0] : getResponse)
}

const getDefaultOpenStatus = async () => {
  const statusResult = await conn.query(`
    SELECT Id FROM status_templates 
    WHERE Title = 'Open' OR Title = 'open' 
    LIMIT 1
  `)
  return statusResult.toArray()[0]?.Id || 'open'
}

const getStatusIdFromItem = async (createdItem, isProcess) => {
  if (isProcess) {
    // For processes, get the last formStatusId from the first form's formStatusHistories
    if (createdItem.forms && createdItem.forms.length > 0 && 
        createdItem.forms[0].formStatusHistories && 
        createdItem.forms[0].formStatusHistories.length > 0) {
      const histories = createdItem.forms[0].formStatusHistories
      return histories[histories.length - 1].formStatusId
    }
  } else {
    // For issues, get the last issueStatusId from issueStatusHistories
    if (createdItem.issueStatusHistories && createdItem.issueStatusHistories.length > 0) {
      const histories = createdItem.issueStatusHistories
      return histories[histories.length - 1].issueStatusId
    }
  }
  
  // Fallback to default open status
  return await getDefaultOpenStatus()
}

const insertProcessFormItems = async (createdItem, statusId) => {
  for (const form of createdItem.forms) {
    // Get statusId for this specific form
    let formStatusId = statusId
    if (form.formStatusHistories && form.formStatusHistories.length > 0) {
      const histories = form.formStatusHistories
      formStatusId = histories[histories.length - 1].formStatusId
    }
    
    await conn.query(`
      INSERT INTO items (
        Id, 
        LocationId,
        CreatedDate,
        StatusId,
        SubItemId,
        Type
      ) VALUES (
        '${form.id}',
        '${selectedLocation.value.Id}',
        CURRENT_TIMESTAMP,
        '${formStatusId}',
        '${form.id}',
        'subtask-item'
      )
    `)
  }
}

const insertSingleItem = async (itemId, statusId) => {
  await conn.query(`
    INSERT INTO items (
      Id, 
      LocationId,
      CreatedDate,
      StatusId,
      SubItemId,
      Type
    ) VALUES (
      '${itemId}',
      '${selectedLocation.value.Id}',
      CURRENT_TIMESTAMP,
      '${statusId}',
      '${props.selectedSubtask.id}',
      'subtask-item'
    )
  `)
}

const updateSubtaskInDB = async (itemId) => {
  await conn.query(`
    UPDATE subtasks 
    SET itemId = '${itemId}'
    WHERE id = '${props.selectedSubtask.id}'
  `)
}

const createItemForSubtask = async (location) => {
  try {
    // 1. Get current drawing
    const drawing = await getCurrentDrawing()

    // 2. Get item template and generate ID
    const itemTemplate = await getItemTemplate(props.selectedSubtask.itemTemplateCode, props.selectedSubtask.itemType)
    if (!itemTemplate) {
      console.error('Item template not found for code:', props.selectedSubtask.itemTemplateCode)
      return
    }
    
    const generatedId = idGenerator.generate()
    const isProcess = props.selectedSubtask.itemType === 'process'
    
    // 3. Build API endpoint and payload
    const endpoint = getApiEndpoint(isProcess)
    const payload = buildItemPayload(generatedId, location, drawing, itemTemplate, props.selectedSubtask)
    
    // 4. Create the item via API
    const itemId = await createItemViaAPI(isProcess, endpoint, payload)
    
    // 5. Update the subtask with the item ID
    await updateSubtaskWithItem(itemId)
    
    // 6. Retrieve the created item
    const createdItem = await retrieveCreatedItem(endpoint, itemId, isProcess)

    // 7. Get status ID from the created item
    const statusId = await getStatusIdFromItem(createdItem, isProcess)
    
    // 8. Insert items records into database
    if (isProcess && createdItem.forms && createdItem.forms.length > 0) {
      await insertProcessFormItems(createdItem, statusId)
    } else {
      await insertSingleItem(itemId, statusId)
    }
    
    // 9. Update subtask in database
    await updateSubtaskInDB(itemId)
    
    // 10. Open the appropriate viewer
    const viewerUrl = buildViewerUrl(isProcess, itemId, createdItem)
    if (viewerUrl) {
      window.open(viewerUrl, '_blank')
    }
    
  } catch (error) {
    console.error('Error creating item:', error)
    throw error
  }
}

const linkSubtask = async () => {
  if (!props.selectedSubtask || !selectedLocation.value) return;
  
  isLinking.value = true;
  
  try {
    // Create the item for the subtask
    await createItemForSubtask(selectedLocation.value);
    
    // Emit completion event to trigger refresh
    emit('subtask-linked');
    
    closeDialog();
    
    // Show success message
    console.log('Item created and linked to subtask successfully');
    
  } catch (error) {
    console.error('Error creating item for subtask:', error);
  } finally {
    isLinking.value = false;
  }
}

const closeDialog = () => {
  // Reset state
  currentStep.value = 1
  selectedLocation.value = null
  selectedProfile.value = null
  externalReference.value = ''
  profileOptions.value = []
  currentDrawingId.value = null
  isLinking.value = false
  
  showDialog.value = false
}
</script>

<style scoped>
.v-stepper {
  box-shadow: none;
}
</style>